/**
 * Auth state for the web app.
 *
 * Port of flock-expo/src/context/AuthContext.tsx adapted for web:
 * - Login flows live here (web has no separate auth screens state machine):
 *   Firebase sign-in → backend session → store tokens → fetch /auth/me →
 *   force ID-token refresh to pick up custom claims (organizationId, role,
 *   pgUserId) that Firestore security rules depend on.
 * - switchOrganization keeps mobile's hardening: monotonic sequence ref to
 *   supersede concurrent switches, claims-verification loop before any state
 *   update, storage rollback on failure. These guards are load-bearing —
 *   see the cross-org race fixes shipped in PR #486/#487.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import { api, refreshAccessToken, registerSubscriptionLockHandler } from '@/lib/apiClient';
import { queryClient } from '@/lib/queryClient';
import {
  clearAuthTokens,
  getCurrentOrgId,
  getLastSelectedGroupId,
  getRefreshToken,
  getToken,
  isTokenExpired,
  setCurrentOrgId,
  setLastSelectedGroupId,
  setRefreshToken,
  setToken,
} from '@/lib/storage';
import type {
  Group,
  Organization,
  ProfileCompleteness,
  Role,
  User,
  UserOrganization,
} from '@flock-types/auth.types';
import type { AuthResponse, MeResponse, SwitchOrganizationResponse } from '@/types/api.types';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  organizations: UserOrganization[];
  currentOrganization: Organization | null;
  currentGroup: Group | null;
  role: Role['role'] | null;
  error: string | null;
  profileCompleteness: ProfileCompleteness | null;
  /** True when the user is authenticated but has no organization yet. */
  pendingOrganization: boolean;
}

export interface AuthContextValue extends AuthState {
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchOrganization: (orgId: string, targetGroupId?: string) => Promise<void>;
  setCurrentGroup: (group: Group | null) => void;
  isSwitchingOrg: boolean;
  /**
   * Resolves true once Firebase custom claims match the current org —
   * Firestore subscriptions must gate on this to avoid permission-denied
   * storms while claims propagate.
   */
  claimsReady: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ROLE_HIERARCHY: Role['role'][] = [
  'org_admin',
  'campus_pastor',
  'ministry_director',
  'group_leader',
  'group_member',
];

function computeRole(orgs: UserOrganization[], currentOrgId: string | null): Role['role'] | null {
  if (!currentOrgId) return null;
  const membership = orgs.find((o) => o.organization_id === currentOrgId);
  if (!membership) return null;
  for (const role of ROLE_HIERARCHY) {
    if (membership.roles.some((r) => r.role === role)) return role;
  }
  return null;
}

const LOGGED_OUT_STATE: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  organizations: [],
  currentOrganization: null,
  currentGroup: null,
  role: null,
  error: null,
  profileCompleteness: null,
  pendingOrganization: false,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...LOGGED_OUT_STATE, isLoading: true });
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false);
  const [claimsReady, setClaimsReady] = useState(false);

  const switchInFlightRef = useRef(0);
  const switchSeqRef = useRef(0);
  const isFetchingRef = useRef(false);

  /** Wait until Firebase custom claims report the expected org. */
  const verifyClaims = useCallback(async (expectedOrgId: string): Promise<boolean> => {
    const maxAttempts = 15;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const tokenResult = await auth.currentUser?.getIdTokenResult(true);
        if (tokenResult?.claims?.organizationId === expectedOrgId) {
          return true;
        }
      } catch (e) {
        console.warn('[Auth] Claims check failed (attempt', i + 1, '):', e);
      }
      if (i < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    return false;
  }, []);

  /** Fetch /auth/me, resolve org+group context, and commit to state. */
  const fetchUserData = useCallback(async (): Promise<void> => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      // Pass the stored org so the backend sets Firebase claims to the org the
      // user was last using (not organizations[0]) — Firestore rules depend on it.
      let currentOrgId = getCurrentOrgId();
      const meUrl = currentOrgId
        ? `/auth/me?organizationId=${currentOrgId}`
        : '/auth/me';
      const data = await api.get<MeResponse>(meUrl);
      const { user, organizations, profile_completeness } = data;

      if (user.status === 'pending_org' || organizations.length === 0) {
        setState({
          ...LOGGED_OUT_STATE,
          isAuthenticated: true,
          user,
          pendingOrganization: true,
        });
        return;
      }

      // Fall back to first active org if the stored one is stale/removed
      if (
        !currentOrgId ||
        !organizations.find((o) => o.organization_id === currentOrgId && o.status === 'active')
      ) {
        const activeOrg = organizations.find((o) => o.status === 'active');
        currentOrgId = activeOrg ? activeOrg.organization_id : organizations[0].organization_id;
        setCurrentOrgId(currentOrgId);
      }

      // Pick up the custom claims the backend just set during /auth/me
      setClaimsReady(false);
      const verified = await verifyClaims(currentOrgId);
      if (!verified) {
        console.warn('[Auth] Claims did not match stored org after /auth/me');
      }
      setClaimsReady(true);

      const membership = organizations.find((o) => o.organization_id === currentOrgId);
      const currentOrg = membership?.organization || null;

      // Restore last selected group for this user (must still be available)
      let currentGroup: Group | null = null;
      const storedGroupId = getLastSelectedGroupId(user.id);
      if (storedGroupId) {
        currentGroup = membership?.groups.find((g) => g.id === storedGroupId) || null;
      }
      if (!currentGroup && membership?.groups && membership.groups.length > 0) {
        currentGroup = membership.groups[0];
        setLastSelectedGroupId(user.id, currentGroup.id);
      }

      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        organizations,
        currentOrganization: currentOrg,
        currentGroup,
        role: computeRole(organizations, currentOrgId),
        error: null,
        profileCompleteness: profile_completeness,
        pendingOrganization: false,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      console.warn('[Auth] fetchUserData failed:', status, error?.message);
      setState((prev) => {
        // Keep stale session on transient errors if we already have data
        if (prev.isAuthenticated && prev.user) {
          return { ...prev, isLoading: false };
        }
        return {
          ...LOGGED_OUT_STATE,
          error:
            status === 401 || status === 403
              ? 'Session expired. Please sign in again.'
              : 'Could not connect. Please try again.',
        };
      });
      throw error;
    } finally {
      isFetchingRef.current = false;
    }
  }, [verifyClaims]);

  // ── Session restore on page load ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Wait for Firebase Auth to resolve its persisted session
        await auth.authStateReady();
        if (cancelled) return;

        const token = getToken();
        if (!token && !getRefreshToken()) {
          setState(LOGGED_OUT_STATE);
          return;
        }

        // Pre-refresh if the access token is expired (avoids 401 round-trip)
        if (!token || isTokenExpired(token)) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            clearAuthTokens();
            setState(LOGGED_OUT_STATE);
            return;
          }
        }

        await fetchUserData();
      } catch {
        // fetchUserData already committed an error state
      } finally {
        if (!cancelled) {
          setState((prev) => (prev.isLoading ? { ...prev, isLoading: false } : prev));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Proactive refresh + claims re-verify when the tab regains focus ───
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const REFRESH_THRESHOLD_MS = 10 * 60 * 1000;

    const checkAndRefreshToken = async () => {
      try {
        const token = getToken();
        if (!token) return;
        if (isTokenExpired(token, REFRESH_THRESHOLD_MS)) {
          await refreshAccessToken();
        }

        // Another session (phone, admin panel) may have rotated the global
        // per-user claims to a different org while this tab was hidden.
        if (auth.currentUser) {
          const storedOrgId = getCurrentOrgId();
          if (storedOrgId) {
            let tokenResult = await auth.currentUser.getIdTokenResult(false);
            if (tokenResult.claims.organizationId !== storedOrgId) {
              tokenResult = await auth.currentUser.getIdTokenResult(true);
            }
            if (tokenResult.claims.organizationId !== storedOrgId) {
              console.log('[Auth] Claims org mismatch on resume, resyncing via /auth/me');
              await fetchUserData().catch(() => {});
            }
          }
        }
      } catch (e) {
        console.warn('[Auth] Proactive refresh failed (non-blocking):', e);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefreshToken();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    checkAndRefreshToken();
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [state.isAuthenticated, fetchUserData]);

  // ── Login flows ────────────────────────────────────────────────────────
  const completeBackendLogin = useCallback(
    async (response: AuthResponse): Promise<void> => {
      const data = response.data;
      if (!data?.token) {
        throw new Error('Login response missing token');
      }
      setToken(data.token);
      if (data.refreshToken) setRefreshToken(data.refreshToken);

      if (data.pendingOrganization) {
        setState({
          ...LOGGED_OUT_STATE,
          isAuthenticated: true,
          user: (data.user as User) ?? null,
          pendingOrganization: true,
        });
        return;
      }

      // Honor the stored org if the user still belongs to it; otherwise take
      // the backend's currentOrganizationId.
      const storedOrgId = getCurrentOrgId();
      const stillMember = storedOrgId
        ? data.organizations?.some((o) => o.organizationId === storedOrgId)
        : false;
      if (!stillMember && data.currentOrganizationId) {
        setCurrentOrgId(data.currentOrganizationId);
      }

      await fetchUserData();
    },
    [fetchUserData]
  );

  const loginWithEmail = useCallback(
    async (email: string, password: string): Promise<void> => {
      // Firebase sign-in is required for Firestore access even though the
      // backend verifies the password independently.
      await signInWithEmailAndPassword(auth, email, password);
      const response = await api.post<AuthResponse>('/auth/login', { email, password });
      await completeBackendLogin(response);
    },
    [completeBackendLogin]
  );

  const loginWithGoogle = useCallback(async (): Promise<void> => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const idToken = await result.user.getIdToken();
    const response = await api.post<AuthResponse>('/auth/google', {
      idToken,
      platform: 'web',
    });
    await completeBackendLogin(response);
  }, [completeBackendLogin]);

  const loginWithApple = useCallback(async (): Promise<void> => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(auth, provider);
    const idToken = await result.user.getIdToken();
    // Apple only provides name/email on the FIRST ever sign-in — pass through
    // whatever Firebase exposes so the backend can store it then.
    const response = await api.post<AuthResponse>('/auth/apple', {
      idToken,
      email: result.user.email,
      name: result.user.displayName,
      appleUserId: result.user.providerData?.[0]?.uid,
      platform: 'web',
    });
    await completeBackendLogin(response);
  }, [completeBackendLogin]);

  // ── Sign out ───────────────────────────────────────────────────────────
  const signOut = useCallback(async (): Promise<void> => {
    try {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken }).catch((e) => {
          console.warn('[Auth] Backend logout failed (non-fatal):', e?.message);
        });
      }
    } finally {
      clearAuthTokens();
      await firebaseSignOut(auth).catch(() => {});
      setClaimsReady(false);
      setState(LOGGED_OUT_STATE);
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));
    await fetchUserData().catch(() => {});
  }, [fetchUserData]);

  // When a REST call 403s with SUBSCRIPTION_SUSPENDED mid-session, refresh the
  // session (debounced in the api layer) so per-org lock state propagates and
  // the inline lock UI renders. Never signs out — locking is per-org (PR 6).
  useEffect(() => {
    registerSubscriptionLockHandler(() => {
      void fetchUserData().catch(() => {});
    });
    return () => registerSubscriptionLockHandler(null);
  }, [fetchUserData]);

  // ── Org switching (port of mobile's hardened sequence) ────────────────
  const switchOrganization = useCallback(
    async (orgId: string, targetGroupId?: string): Promise<void> => {
      const mySeq = ++switchSeqRef.current;
      switchInFlightRef.current += 1;
      setIsSwitchingOrg(true);
      setClaimsReady(false);
      try {
        const orgMembership = state.organizations.find((o) => o.organization_id === orgId);
        const currentOrg = orgMembership?.organization || null;
        const role = computeRole(state.organizations, orgId);
        const userId = state.user?.id;

        const previousOrgId = getCurrentOrgId();

        try {
          // Persist org BEFORE the call so the 401 interceptor reads the right org
          setCurrentOrgId(orgId);
          const response = await api.post<SwitchOrganizationResponse>(
            '/auth/switch-organization',
            { organizationId: orgId }
          );
          const data = response.data;
          const token = data?.accessToken || data?.token;
          // Supersession gate: a newer switch has started — abandon our writes
          if (mySeq !== switchSeqRef.current) return;
          if (token) setToken(token);
          if (data?.refreshToken) setRefreshToken(data.refreshToken);
        } catch (err) {
          // Roll back so storage matches reality (claims still point at old org)
          if (previousOrgId) {
            setCurrentOrgId(previousOrgId);
          }
          throw err;
        }

        // Verify claims BEFORE committing state so Firestore hooks fire with
        // matching claims on their very first subscription attempt.
        const verified = await verifyClaims(orgId);
        if (!verified) {
          console.warn('[Auth] Claims did not propagate after switch, proceeding anyway');
        }

        if (mySeq !== switchSeqRef.current) return;

        let newCurrentGroup: Group | null = null;
        if (orgMembership?.groups && orgMembership.groups.length > 0) {
          const requestedGroup = targetGroupId
            ? orgMembership.groups.find((g) => g.id === targetGroupId)
            : null;
          newCurrentGroup = requestedGroup || orgMembership.groups[0];
          if (userId) setLastSelectedGroupId(userId, newCurrentGroup.id);
        } else if (userId) {
          setLastSelectedGroupId(userId, null);
        }

        // Drop all cached queries from the previous org before any consumer
        // refetches under the new org context.
        queryClient.clear();

        setClaimsReady(true);
        setState((prev) => ({
          ...prev,
          currentOrganization: currentOrg,
          currentGroup: newCurrentGroup,
          role,
        }));
      } finally {
        switchInFlightRef.current -= 1;
        if (switchInFlightRef.current === 0) {
          setIsSwitchingOrg(false);
        }
      }
    },
    [state.organizations, state.user?.id, verifyClaims]
  );

  const setCurrentGroup = useCallback(
    (group: Group | null): void => {
      const userId = state.user?.id;
      if (userId) setLastSelectedGroupId(userId, group?.id ?? null);
      setState((prev) => ({ ...prev, currentGroup: group }));
    },
    [state.user?.id]
  );

  const value: AuthContextValue = {
    ...state,
    loginWithEmail,
    loginWithGoogle,
    loginWithApple,
    signOut,
    refreshUser,
    switchOrganization,
    setCurrentGroup,
    isSwitchingOrg,
    claimsReady,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
